import dayjs from 'dayjs';
import {useAtom} from 'jotai';
import {useEffect, useRef, useState} from 'react';
import Markdown from 'react-markdown';
import styled from 'styled-components';
import toast from '@/components/HotToast';
import {FULL_DATE_FORMAT} from '@/constants';
import {useScrollDepth} from '@/hooks/scrollDepth';
import {useGA4WithParams} from '@/hooks/tracking';
import {AiVoteStateType} from '@/map/@types';
import {useFetchAiPrompt} from '@/map/api';
import {aiVoteListAtom} from '@/map/atom';
import {AnalysisTypeText} from '@/map/constants';
import {palette} from '@/styles';
import {copyText} from '@/utils';
import {AreaInfo} from './AreaInfo';
import {LoadingPrompt} from './LoadingPrompt';
import {PanelFooter} from './PanelFooter';
import {BlueInfoText, ScrollBody} from './StyledComponents';
import {AiSubHeader} from './SubHeader';

interface IProps {
  map?: naver.maps.Map;
  id: string;
  /** 패널이 실제로 펼쳐져 있는지. 닫혀도 언마운트되지 않으므로 수집 게이트로 쓴다 */
  open?: boolean;
  analysisType?: AnalysisTypeText;
}

const CONTENT_ID = 'myfc-ai-prompt-content-body';

export const PromptMessage = ({map, id, open, analysisType}: IProps) => {
  const [aiVoteList, setAiVoteList] = useAtom(aiVoteListAtom);
  const voteState = aiVoteList[id];
  const {data, isPending, isError, dataUpdatedAt, refetch} =
    useFetchAiPrompt(id);

  const timer = useRef<NodeJS.Timer>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isCopied, setCopied] = useState<boolean>();
  const [emdName, setEmdName] = useState<string>();

  const tracking = useGA4WithParams();

  const content = data?.result;

  // 닫았다 다시 연 경우도 새로 읽는 것으로 보고 진행률을 다시 수집한다.
  // 마운트 시점에 이미 열려 있었다면 그 자체가 첫 열람이므로 세션을 올리지 않는다.
  const [readSession, setReadSession] = useState<number>(0);
  const wasOpenRef = useRef<boolean>(!!open);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setReadSession((prev) => prev + 1);
    }

    wasOpenRef.current = !!open;
  }, [open]);

  // 해설을 어디까지 읽었는지(스크롤 진행률) 단계별로 1회씩 수집
  useScrollDepth({
    contentRef,
    getScrollContainer: () => scrollRef.current,
    enabled: !!open && !!content?.length,
    resetKey: `${id}:${readSession}`,
    onReach: (percent) => {
      tracking('AI_analysis_scroll', {
        analysis_type: analysisType,
        area: emdName,
        percent,
      });
    },
  });

  const onVote = (vote: AiVoteStateType) => {
    tracking(`AI_analysis_reaction_${vote === 'up' ? 'like' : 'dislike'}`, {
      area: emdName,
    });
    setAiVoteList((prev) => ({
      ...prev,
      [id]: vote,
    }));
  };

  const refetchPrompt = () => {
    refetch();
  };

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = undefined;
    }
  };

  const copyAiPrompt = () => {
    const text = document.getElementById(CONTENT_ID)?.innerText;
    tracking('AI_analysis_contents_copy', {area: emdName});

    if (text) {
      const success = copyText(text);

      if (success) {
        setCopied(true);
        toast.success('내용이 복사되었습니다');

        timer.current = setTimeout(() => {
          setCopied(false);
          timer.current = undefined;
        }, 1500);
      }
    }
  };

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, []);

  if (isPending) {
    return <LoadingPrompt />;
  }

  return (
    <>
      <AiSubHeader
        isCopied={isCopied}
        onCopyClick={isError ? undefined : copyAiPrompt}
      >
        {isError ? (
          <BlueInfoText>오류가 발생했어요</BlueInfoText>
        ) : (
          !!dataUpdatedAt &&
          `${dayjs(dataUpdatedAt).format(FULL_DATE_FORMAT)} 기준`
        )}
      </AiSubHeader>
      <ScrollBody ref={scrollRef}>
        <ContentWrapper ref={contentRef}>
          <AreaInfo
            id={id}
            map={map}
            isError={isError}
            afterFetchEmdName={(name) => setEmdName(name)}
            onRefetch={refetchPrompt}
          />
          {!!content?.length && (
            <>
              <Note>
                상권 데이터 해설 가이드라인은 AI 모델에 의해 다양한 데이터를
                기반으로 생성되고 있어서 부정확할 수 있어요. 정확한 정보가
                전달될 수 있도록 최선을 다하고 있으나 투자 결정 시 제공된 정보에
                대한 이용 여부는 신중히 판단해 주세요.
              </Note>
              <Divider />
              <Content id={CONTENT_ID}>
                <Markdown>{content}</Markdown>
              </Content>
            </>
          )}
        </ContentWrapper>
      </ScrollBody>
      <PanelFooter voteState={voteState} onVote={onVote} />
    </>
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const Content = styled.div`
  ul {
    padding-inline-start: 16px;
  }
`;

const Note = styled.div`
  color: ${palette.gray600};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.6;
`;

const Divider = styled.div`
  background-color: ${palette.gray200};
  height: 1px;
  width: 100%;
`;
